// src/components/roles/RoleForm.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Role } from './RoleApi';
import { createRole, updateRole, fetchRoleById } from './RoleApi';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchModules } from '../modules/ModuleApi';
import { createPermissionsForRole, getPermissionsByRoleId } from './rolemoduleperssion/RoleModulePermissionApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faUndo,
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faCheckDouble,
  faBan,
} from '@fortawesome/free-solid-svg-icons';

import { RoleModulePermission } from './RoleApi';

type BooleanKeys<T> = {
  [K in keyof T]-?: T[K] extends boolean ? K : never
}[keyof T];

type PermBooleanKey = BooleanKeys<RoleModulePermission>;

const CORE_FIELDS: PermBooleanKey[] = [
  'canCreate', 'canView', 'canEdit', 'canDelete', 'canReport',
];

const EXTENDED_FIELDS: PermBooleanKey[] = [
  'canAssign', 'canComment', 'canUpload', 'canApprove', 'canReject',
  'canChangeStatus', 'canArchive', 'canRestore', 'canExport', 'canImport', 'canShare',
];

const UI_FIELDS: PermBooleanKey[] = [
  'visibleInSidebar', 'visibleInDashboard',
];

const ALL_TOGGLE_KEYS: PermBooleanKey[] = [
  ...CORE_FIELDS, ...EXTENDED_FIELDS, ...UI_FIELDS,
];

interface RoleFormProps {
  viewMode?: boolean;
}

const RoleForm: React.FC<RoleFormProps> = ({ viewMode = false }) => {
  const [form, setForm] = useState<Role>({ name: '', description: '' });
  const [initialForm, setInitialForm] = useState<Role>({ name: '', description: '' });

  const [permissions, setPermissions] = useState<RoleModulePermission[]>([]);
  const [initialPermissions, setInitialPermissions] = useState<RoleModulePermission[]>([]);

  const [expandedRow, setExpandedRow] = useState<Record<string, boolean>>({});
  const [isViewMode, setIsViewMode] = useState(viewMode);
  const { id } = useParams();
  const navigate = useNavigate();

  // derived for quick UX: if any permission changed, enable save/update
  const isDirty = useMemo(() => {
    if (form.name !== initialForm.name || (form.description || '') !== (initialForm.description || '')) {
      return true;
    }
    if (permissions.length !== initialPermissions.length) return true;
    // shallow diff is enough since ordering is the same (by moduleId)
    for (let i = 0; i < permissions.length; i++) {
      const a = permissions[i];
      const b = initialPermissions[i];
      if (!b || a.moduleId !== b.moduleId) return true;
      for (const k of ALL_TOGGLE_KEYS) {
        if (a[k] !== b[k]) return true;
      }
    }
    return false;
  }, [form, initialForm, permissions, initialPermissions]);

  useEffect(() => {
    const loadData = async () => {
      if (id) {
        if (window.location.pathname.includes('view')) setIsViewMode(true);

        const roleRes = await fetchRoleById(id);
        setForm(roleRes.data);
        setInitialForm(roleRes.data);

        // Existing permissions for this role
        const permissionRes = await getPermissionsByRoleId(id);

        const perms: RoleModulePermission[] = permissionRes.data.map((p: any) => ({
          roleId: p.roleId,
          moduleId: p.moduleId,
          moduleName: p.moduleName,

          // core
          canCreate: !!p.canCreate,
          canView: !!p.canView,
          canEdit: !!p.canEdit,
          canDelete: !!p.canDelete,
          canReport: !!p.canReport,

          // extended (default false if BE not yet returning them)
          canAssign: !!p.canAssign,
          canComment: !!p.canComment,
          canUpload: !!p.canUpload,
          canApprove: !!p.canApprove,
          canReject: !!p.canReject,
          canChangeStatus: !!p.canChangeStatus,
          canArchive: !!p.canArchive,
          canRestore: !!p.canRestore,
          canExport: !!p.canExport,
          canImport: !!p.canImport,
          canShare: !!p.canShare,

          // UI flags
          visibleInSidebar: !!p.visibleInSidebar,
          visibleInDashboard: !!p.visibleInDashboard,
        }));

        setPermissions(perms);
        setInitialPermissions(perms);
      } else {
        // New role: load modules and initialize all flags to false
        const modulesRes = await fetchModules();
        const perms: RoleModulePermission[] = (modulesRes.data ?? modulesRes).map((m: any) => ({
          roleId: '',
          moduleId: m.id,
          moduleName: m.name,

          // core
          canCreate: false,
          canView: false,
          canEdit: false,
          canDelete: false,
          canReport: false,

          // extended
          canAssign: false,
          canComment: false,
          canUpload: false,
          canApprove: false,
          canReject: false,
          canChangeStatus: false,
          canArchive: false,
          canRestore: false,
          canExport: false,
          canImport: false,
          canShare: false,

          // UI flags
          visibleInSidebar: false,
          visibleInDashboard: false,
        }));
        setPermissions(perms);
        setInitialPermissions(perms);
      }
    };

    loadData();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const setRowField = (rowIdx: number, field: PermBooleanKey, value: boolean) => {
    setPermissions((prev) => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [field]: value };
      return next;
    });
  };

  const handlePermissionChange = (
    index: number,
    field: PermBooleanKey,
    value: boolean
  ) => {
    setRowField(index, field, value);
  };

  const isAllSelected = (row: RoleModulePermission) =>
    ALL_TOGGLE_KEYS.every((k) => !!row[k]);

  const isPartiallySelected = (row: RoleModulePermission) => {
    const t = ALL_TOGGLE_KEYS.filter((k) => !!row[k]).length;
    return t > 0 && t < ALL_TOGGLE_KEYS.length;
  };

  const setAllFlagsFor = (rowIdx: number, value: boolean) => {
    setPermissions((prev) => {
      const next = [...prev];
      const row = { ...next[rowIdx] };
      ALL_TOGGLE_KEYS.forEach((k) => (row[k] = value));
      next[rowIdx] = row;
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let roleId = id;

    if (!id) {
      const roleRes = await createRole(form);
      roleId = roleRes.data.id;
    } else {
      await updateRole(id!, form);
    }

    const permissionsPayload: RoleModulePermission[] = permissions.map(p => ({
      ...p,
      roleId: roleId!,
    }));

    await createPermissionsForRole(roleId!, permissionsPayload);
    navigate('/admin/roles');
  };

  const handleReset = () => {
    setForm(initialForm);
    setPermissions(JSON.parse(JSON.stringify(initialPermissions)));
  };

  const handleBack = () => {
    navigate('/admin/roles');
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white shadow rounded">
      <h2 className="text-xl font-semibold mb-4">
        {isViewMode ? 'View Role' : id ? 'Edit Role' : 'Add Role'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium">Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded"
              disabled={isViewMode}
            />
          </div>

          <div>
            <label className="block font-medium">Description</label>
            <textarea
              name="description"
              value={form.description || ''}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              disabled={isViewMode}
            />
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-xl font-bold mb-3">Module Permissions</h3>

          <div className="w-full overflow-auto border rounded">
            <table className="min-w-full bg-white text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-2 py-2 w-10"></th>
                  <th className="border px-2 py-2 text-left">Module</th>

                  {/* Core columns */}
                  {CORE_FIELDS.map((f) => (
                    <th key={f} className="border px-2 py-2 capitalize">{f.replace(/([A-Z])/g, " $1")}</th>
                  ))}

                  {/* UI short columns */}
                  {UI_FIELDS.map((f) => (
                    <th key={f} className="border px-2 py-2">{f === 'visibleInSidebar' ? 'Sidebar' : 'Dashboard'}</th>
                  ))}

                  {/* Select All column */}
                  <th className="border px-2 py-2">Select All</th>
                </tr>
              </thead>

              <tbody>
                {permissions.map((perm, index) => {
                  const isOpen = !!expandedRow[perm.moduleId];
                  const allSelected = isAllSelected(perm);
                  const partial = isPartiallySelected(perm);

                  return (
                    <React.Fragment key={perm.moduleId}>
                      <tr>
                        {/* expand/collapse */}
                        <td className="border px-2 py-2 text-center">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedRow((s) => ({ ...s, [perm.moduleId]: !s[perm.moduleId] }))
                            }
                            className="border rounded px-2 py-1 text-slate-700 hover:bg-slate-50"
                            title={isOpen ? "Hide details" : "Show details"}
                          >
                            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                          </button>
                        </td>

                        {/* module name */}
                        <td className="border px-2 py-2">{perm.moduleName}</td>

                        {/* core fields */}
                        {CORE_FIELDS.map((field) => (
                          <td key={field} className="border px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={!!perm[field]}
                              onChange={(e) => handlePermissionChange(index, field, e.target.checked)}
                              disabled={isViewMode}
                            />
                          </td>
                        ))}

                        {/* UI flags inline */}
                        {UI_FIELDS.map((field) => (
                          <td key={field} className="border px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={!!perm[field]}
                              onChange={(e) => handlePermissionChange(index, field, e.target.checked)}
                              disabled={isViewMode}
                            />
                          </td>
                        ))}

                        {/* Select all / none */}
                        <td className="border px-2 py-2">
                          <div className="flex items-center gap-2 justify-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-indigo-600"
                              checked={allSelected}
                              ref={(el) => { if (el) el.indeterminate = partial && !allSelected; }}
                              onChange={(e) => setAllFlagsFor(index, e.target.checked)}
                              disabled={isViewMode}
                              title="Select/Unselect all permissions for this module"
                            />
                            <button
                              type="button"
                              className="text-xs border rounded px-2 py-1 hover:bg-indigo-50 text-indigo-700 border-indigo-200"
                              onClick={() => setAllFlagsFor(index, true)}
                              disabled={isViewMode}
                              title="Select all"
                            >
                              <FontAwesomeIcon icon={faCheckDouble} className="mr-1" />
                              All
                            </button>
                            <button
                              type="button"
                              className="text-xs border rounded px-2 py-1 hover:bg-slate-50 text-slate-700 border-slate-200"
                              onClick={() => setAllFlagsFor(index, false)}
                              disabled={isViewMode}
                              title="Unselect all"
                            >
                              <FontAwesomeIcon icon={faBan} className="mr-1" />
                              None
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Extended details row */}
                      {isOpen && (
                        <tr className="bg-slate-50/50">
                          <td className="border px-2 py-2" />
                          <td className="border px-2 py-2" colSpan={CORE_FIELDS.length + UI_FIELDS.length + 2}>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-semibold mb-2 text-slate-700">
                                  Extended Actions
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  {EXTENDED_FIELDS.map((field) => (
                                    <label key={field} className="inline-flex items-center gap-2 text-sm">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 accent-indigo-600"
                                        checked={!!perm[field]}
                                        onChange={(e) => handlePermissionChange(index, field, e.target.checked)}
                                        disabled={isViewMode}
                                      />
                                      <span className="capitalize">
                                        {field.replace(/([A-Z])/g, " $1")}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <h4 className="text-sm font-semibold mb-2 text-slate-700">
                                  UI Visibility (Quick)
                                </h4>
                                <div className="grid grid-cols-2 gap-2">
                                  {UI_FIELDS.map((field) => (
                                    <label key={field} className="inline-flex items-center gap-2 text-sm">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 accent-indigo-600"
                                        checked={!!perm[field]}
                                        onChange={(e) => handlePermissionChange(index, field, e.target.checked)}
                                        disabled={isViewMode}
                                      />
                                      <span className="capitalize">
                                        {field.replace(/([A-Z])/g, " $1")}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="bg-gray-500 text-white px-4 py-2 rounded inline-flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>

          {!isViewMode && (
            <>
              <button
                type="button"
                onClick={handleReset}
                className="bg-yellow-500 text-white px-4 py-2 rounded inline-flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faUndo} />
                <span>Reset</span>
              </button>

              <button
                type="submit"
                disabled={!isDirty}
                className={`px-4 py-2 rounded inline-flex items-center gap-2 ${
                  isDirty
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-slate-200 text-slate-500 cursor-not-allowed"
                }`}
                title={isDirty ? "Save changes" : "No changes to save"}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{id ? 'Update' : 'Create'}</span>
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default RoleForm;